import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { getConsent, setConsent } from "@/lib/consent";

const CookieConsent = () => {
  const [bannerOpen, setBannerOpen] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [ga, setGa] = useState(false);
  const [googleAds, setGoogleAds] = useState(false);
  const [metaPixel, setMetaPixel] = useState(false);
  const bannerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const existing = getConsent();
    if (!existing) {
      // Defer to next tick so we don't block first paint
      const t = window.setTimeout(() => setBannerOpen(true), 600);
      return () => window.clearTimeout(t);
    }
    setGa(!!existing.ga);
    setGoogleAds(!!existing.googleAds);
    setMetaPixel(!!existing.metaPixel);
  }, []);

  // The banner is fixed to the bottom, so on small screens it can sit on top of
  // page controls (the Sign In button on /auth). Reserve the space it occupies
  // so nothing underneath stays covered.
  useEffect(() => {
    if (!bannerOpen) {
      document.body.style.paddingBottom = "";
      return;
    }
    const apply = () => {
      const h = bannerRef.current?.offsetHeight ?? 0;
      document.body.style.paddingBottom = h ? `${h + 24}px` : "";
    };
    apply();
    const ro = new ResizeObserver(apply);
    if (bannerRef.current) ro.observe(bannerRef.current);
    window.addEventListener("resize", apply);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", apply);
      document.body.style.paddingBottom = "";
    };
  }, [bannerOpen]);

  useEffect(() => {
    const openHandler = (e: Event) => {
      const c = getConsent();
      const marketingOff = (e as CustomEvent<{ marketingOff?: boolean }>).detail?.marketingOff === true;
      // "Do Not Sell or Share": save marketing off right away (same as Save
      // preferences); the consent-changed event revokes a loaded Meta Pixel.
      if (marketingOff) setConsent({ ga: c?.ga ?? false, googleAds: false, metaPixel: false }, "do-not-sell-link");
      setGa(c?.ga ?? false);
      setGoogleAds(marketingOff ? false : c?.googleAds ?? false);
      setMetaPixel(marketingOff ? false : c?.metaPixel ?? false);
      setPrefsOpen(true);
      setBannerOpen(false);
    };
    window.addEventListener("os:open-cookie-settings", openHandler);
    return () =>
      window.removeEventListener("os:open-cookie-settings", openHandler);
  }, []);

  const acceptAll = () => {
    setConsent({ ga: true, googleAds: true, metaPixel: true }, "banner-accept-all");
    setBannerOpen(false);
    setPrefsOpen(false);
  };

  const rejectAll = () => {
    setConsent({ ga: false, googleAds: false, metaPixel: false }, "banner-reject-all");
    setBannerOpen(false);
    setPrefsOpen(false);
  };

  const savePrefs = () => {
    setConsent({ ga, googleAds, metaPixel }, "preferences-save");
    setBannerOpen(false);
    setPrefsOpen(false);
  };

  return (
    <>
      {bannerOpen && (
        <div
          ref={bannerRef}
          role="dialog"
          aria-label="Cookie consent"
          className="fixed bottom-4 left-4 right-4 z-[9999] mx-auto max-h-[38vh] max-w-3xl overflow-y-auto rounded-xl border-2 border-black bg-white p-4 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] sm:max-h-[70vh] sm:p-6"
          style={{ bottom: "max(1rem, env(safe-area-inset-bottom))" }}
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <h3 className="text-base font-bold text-black sm:text-lg">
                We respect your privacy
              </h3>
              <p className="mt-2 line-clamp-4 text-xs text-gray-700 sm:line-clamp-none sm:text-sm">
                We use strictly necessary cookies to run the app (login,
                support chat). With your permission we also use analytics and
                marketing cookies (Google Ads, Meta Pixel) to measure
                campaigns. You can change or withdraw your choice any time via
                the &ldquo;Cookie Settings&rdquo; link in the footer. See our{" "}
                <Link to="/privacy" className="font-semibold underline">
                  Privacy Policy
                </Link>
                .
              </p>
            </div>
            <button
              onClick={rejectAll}
              aria-label="Reject all and close"
              className="rounded-md p-1 text-gray-500 hover:bg-gray-100"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-end">
            <Button
              variant="outline"
              className="border-2 border-black bg-white text-black hover:bg-gray-100 hover:text-black"
              onClick={() => {
                setPrefsOpen(true);
                setBannerOpen(false);
              }}
            >
              Customize
            </Button>
            <Button
              variant="outline"
              className="border-2 border-black bg-white text-black hover:bg-gray-100 hover:text-black"
              onClick={rejectAll}
            >
              Reject all
            </Button>
            <Button
              className="border-2 border-black bg-primary-surface text-primary-surface-foreground hover:bg-primary-surface/90"
              onClick={acceptAll}
            >
              Accept all
            </Button>
          </div>
        </div>
      )}

      <Dialog open={prefsOpen} onOpenChange={setPrefsOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Cookie preferences</DialogTitle>
            <DialogDescription>
              Choose which cookie categories we may use. Strictly necessary
              cookies are always on because the site cannot function without
              them.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="flex items-start justify-between gap-4 rounded-lg border p-3">
              <div>
                <p className="font-semibold">Strictly necessary</p>
                <p className="text-sm text-muted-foreground">
                  Authentication, security, support chat session, and load
                  balancing. Cannot be turned off.
                </p>
              </div>
              <Switch checked disabled />
            </div>
            <div className="flex items-start justify-between gap-4 rounded-lg border p-3">
              <div>
                <p className="font-semibold">Analytics</p>
                <p className="text-sm text-muted-foreground">
                  Google Analytics product usage and first-party search/AI traffic measurement.
                  With your permission, OS Tools records referral source, landing page and time
                  once per session and sends those details to internal Telegram alerts; no IP or email is included.
                </p>
              </div>
              <Switch checked={ga} onCheckedChange={setGa} />
            </div>
            <div className="flex items-start justify-between gap-4 rounded-lg border p-3">
              <div>
                <p className="font-semibold">Google Ads</p>
                <p className="text-sm text-muted-foreground">
                  Ad campaign measurement and conversion tracking.
                  Provider: Google LLC.
                </p>
              </div>
              <Switch checked={googleAds} onCheckedChange={setGoogleAds} />
            </div>
            <div className="flex items-start justify-between gap-4 rounded-lg border p-3">
              <div>
                <p className="font-semibold">Meta Pixel</p>
                <p className="text-sm text-muted-foreground">
                  Facebook / Instagram ad measurement and retargeting.
                  Provider: Meta Platforms Inc.
                </p>
              </div>
              <Switch checked={metaPixel} onCheckedChange={setMetaPixel} />
            </div>
          </div>

          <DialogFooter className="flex flex-col gap-2 sm:flex-row">
            <Button variant="outline" onClick={rejectAll}>
              Reject all
            </Button>
            <Button variant="outline" onClick={acceptAll}>
              Accept all
            </Button>
            <Button
              className="bg-primary-surface text-primary-surface-foreground hover:bg-primary-surface/90"
              onClick={savePrefs}
            >
              Save preferences
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CookieConsent;